import React, { useCallback, useEffect, useState } from "react";
import { ArrowClockwise, PlusLg } from "react-bootstrap-icons";
import { Badge, Button, Modal, Spinner, Table } from "react-bootstrap";
import { useError } from "../../contexts/ErrorContext";
import CreateCampaignModal from "../../components/campaigns/CreateCampaignModal";
import CampaignTypeEditor from '../../components/campaigns/CampaignTypeEditor';
import { CampaignAPI } from "../../services/api";
import { Campaign } from "../../types";

const STATUS_VARIANT: Record<Campaign["status"], string> = {
  planned: "secondary",
  running: "info",
  finished: "success",
};

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString() : "—";

export const CampaignsPage: React.FC = () => {
  const { setError } = useError();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [summaryCampaign, setSummaryCampaign] = useState<Campaign | null>(null);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await CampaignAPI.getAllCampaigns();
      setCampaigns(response.data);
    } catch (err) {
      setError(`Failed to load campaigns: ${err}`);
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleCreated = (campaign: Campaign) => {
    // Optimistically prepend, then refresh from the server for canonical data.
    setCampaigns((prev) => [campaign, ...prev]);
    loadCampaigns();
  };

  return (
    <div className="m-3">
      <div className="d-flex align-items-center mb-3">
        <h4 className="mb-0 me-3">Campaigns</h4>
        <Button
          variant="outline-secondary"
          size="sm"
          className="d-flex align-items-center me-2"
          onClick={loadCampaigns}
          disabled={loading}
          title="Refresh"
        >
          <ArrowClockwise className="me-1" size={16} />
          Refresh
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="d-flex align-items-center ms-auto"
          onClick={() => setShowCreate(true)}
        >
          <PlusLg className="me-1" size={16} />
          New campaign
        </Button>
      </div>

      {loading && campaigns.length === 0 ? (
        <div className="d-flex align-items-center text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading campaigns...
        </div>
      ) : campaigns.length === 0 ? (
        <p className="text-muted">No campaigns yet. Create one to get started.</p>
      ) : (
        <Table striped hover responsive size="sm" className="align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Network</th>
              <th>Bitrix id</th>
              <th>Status</th>
              <th>Type</th>
              <th>CVAT project</th>
              <th className="text-end">Records</th>
              <th className="text-end">Videos</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.network_slug || c.network_uuid}</td>
                <td>{c.bitrix_id ?? "—"}</td>
                <td>
                  <Badge bg={STATUS_VARIANT[c.status] ?? "secondary"}>{c.status}</Badge>
                </td>
                <td>{c.type}</td>
                <td>{c.cvat_project_id ?? "—"}</td>
                <td className="text-end">{c.record_count ?? 0}</td>
                <td className="text-end">{c.video_count ?? 0}</td>
                <td>{formatDate(c.created_at)}</td>
                <td className="text-end">
                  <Button variant="link" size="sm" className="p-0" onClick={() => setSummaryCampaign(c)}>
                    Summary
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <CreateCampaignModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        onCreated={handleCreated}
      />

      <Modal show={!!summaryCampaign} onHide={() => setSummaryCampaign(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{summaryCampaign?.name}</Modal.Title>
        </Modal.Header>
        {summaryCampaign && (
          <Modal.Body>
            <CampaignTypeEditor key={`${summaryCampaign.id}-${summaryCampaign.type}`} campaign={summaryCampaign} onSaved={updated => {
              setSummaryCampaign(updated);
              setCampaigns(previous => previous.map(item => item.id === updated.id ? updated : item));
            }} />
            <dl className="row mb-0">
              <dt className="col-sm-5">Status</dt>
              <dd className="col-sm-7">
                <Badge bg={STATUS_VARIANT[summaryCampaign.status] ?? "secondary"}>
                  {summaryCampaign.status}
                </Badge>
              </dd>

              <dt className="col-sm-5">Network</dt>
              <dd className="col-sm-7">{summaryCampaign.network_slug || summaryCampaign.network_uuid}</dd>

              <dt className="col-sm-5">Bitrix id</dt>
              <dd className="col-sm-7">{summaryCampaign.bitrix_id ?? "—"}</dd>

              <dt className="col-sm-5">CVAT project</dt>
              <dd className="col-sm-7">{summaryCampaign.cvat_project_id ?? "—"}</dd>

              <dt className="col-sm-5">Records</dt>
              <dd className="col-sm-7">{summaryCampaign.record_count ?? 0}</dd>

              <dt className="col-sm-5">Videos</dt>
              <dd className="col-sm-7">{summaryCampaign.video_count ?? 0}</dd>

              <dt className="col-sm-5">Start date</dt>
              <dd className="col-sm-7">{formatDate(summaryCampaign.start_date)}</dd>

              <dt className="col-sm-5">End date</dt>
              <dd className="col-sm-7">{formatDate(summaryCampaign.end_date)}</dd>

              <dt className="col-sm-5">Created</dt>
              <dd className="col-sm-7">{formatDate(summaryCampaign.created_at)}</dd>
            </dl>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setSummaryCampaign(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CampaignsPage;
